import { useState } from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import SelectAction from 'components/forms/SelectAction';
import { Button } from 'components/ui/Button';
import { BulkTableAction } from './types';

interface TableFooterProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  showInfo: boolean;
  onPageChange: (page: number) => void;
  // Nouvelles options
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
  // Pagination réellement activée (sinon le footer ne sert qu'aux actions groupées)
  paginationEnabled?: boolean;
  // Actions groupées : remplacent l'info de pagination quand des lignes sont sélectionnées
  selectedCount?: number;
  bulkActions?: BulkTableAction<any>[];
  onBulkAction?: (action: BulkTableAction<any>) => void;
}

const TableFooter = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  showInfo,
  onPageChange,
  pageSizeOptions = [10, 15, 20, 25, 50],
  onPageSizeChange,
  paginationEnabled = true,
  selectedCount = 0,
  bulkActions,
  onBulkAction
}: TableFooterProps) => {
  const [pendingActionLabel, setPendingActionLabel] = useState('');

  const bulkVisible = selectedCount > 0 && !!bulkActions && bulkActions.length > 0;
  const paginationVisible =
    paginationEnabled && totalItems > Math.min(...pageSizeOptions);

  // Rien à afficher : ni pagination, ni actions groupées
  if (!paginationVisible && !bulkVisible) return null;

  const handleApply = () => {
    const action = bulkActions?.find(a => a.label === pendingActionLabel);
    if (action) {
      onBulkAction?.(action);
    }
  };

  const getVisiblePages = (): (number | string)[] => {
    const delta = 2;
    const rangeWithDots: (number | string)[] = [];

    // Si on a peu de pages, on affiche tout
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        rangeWithDots.push(i);
      }
      return rangeWithDots;
    }

    // Toujours afficher la première page
    rangeWithDots.push(1);

    // Calcul de la plage autour de la page courante
    const start = Math.max(2, currentPage - delta);
    const end = Math.min(totalPages - 1, currentPage + delta);

    // Ajouter les ellipses après la première page si nécessaire
    if (start > 2) {
      rangeWithDots.push('...');
    }

    // Ajouter les pages du milieu
    for (let i = start; i <= end; i++) {
      rangeWithDots.push(i);
    }

    // Ajouter les ellipses avant la dernière page si nécessaire
    if (end < totalPages - 1) {
      rangeWithDots.push('...');
    }

    // Toujours afficher la dernière page
    if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const visiblePages = getVisiblePages();
  const startIndex = ((currentPage - 1) * pageSize) + 1;
  const endIndex = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="w-full border-t py-4 px-6 bg-white">
      <div className="flex items-center justify-between">

        {/* Slot gauche : actions groupées (si sélection) sinon info pagination */}
        <div className="flex-1">
          {bulkVisible ? (
            <div className="flex items-center gap-2">
              <SelectAction
                options={bulkActions!.map(action => ({
                  value: action.label,
                  label: action.label
                }))}
                value={pendingActionLabel}
                onChange={setPendingActionLabel}
                placeholder="Actions"
                className="min-w-40"
              />
              <Button
                color="primary"
                disabled={!pendingActionLabel}
                onClick={handleApply}
              >
                Appliquer
              </Button>
            </div>
          ) : (
            showInfo && paginationVisible && (
              <div className="text-sm text-gray-600">
                Affichage de <span className="font-medium">{startIndex}</span> à{' '}
                <span className="font-medium">{endIndex}</span> sur{' '}
                <span className="font-medium">{totalItems}</span> résultats
              </div>
            )
          )}
        </div>

        {/* Pagination au centre */}
        {paginationVisible && (
          <div className="flex items-center justify-center">
            <div className="flex items-center gap-1">
              {/* Bouton précédent */}
              <button
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="group flex items-center justify-center w-10 h-10 text-gray-500 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed hover:text-primary hover:scale-110"
                aria-label="Page précédente"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              {/* Pages */}
              {visiblePages.map((page, index) => (
                page === '...' ? (
                  <div
                    key={`dots-${index}`}
                    className="flex items-center justify-center w-10 h-10 text-gray-400"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </div>
                ) : (
                  <button
                    key={`page-${page}`}
                    onClick={() => onPageChange(page as number)}
                    className={`w-9 h-9 rounded-lg font-medium text-sm transition-all duration-200 transform hover:scale-105 ${currentPage === page
                      ? 'bg-primary text-white shadow-lg shadow-primary/20'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-primary'
                      }`}
                    aria-label={`Page ${page}`}
                    aria-current={currentPage === page ? 'page' : undefined}
                  >
                    {page}
                  </button>
                )
              ))}

              {/* Bouton suivant */}
              <button
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="group flex items-center justify-center w-10 h-10 text-gray-500 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed hover:text-primary hover:scale-110"
                aria-label="Page suivante"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Info et sélecteur de taille à droite */}
        <div className="flex items-center gap-4 flex-1">

          {/* Sélecteur de taille de page */}
          {paginationVisible && onPageSizeChange && pageSizeOptions && pageSizeOptions.length > 1 && (
            <div className="flex items-center justify-end grow gap-2 text-sm text-gray-600">
              <span>Afficher:</span>
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-primary focus:border-primary bg-white"
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span>par page</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TableFooter;
